export type LegalBlock =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "table"; headers: string[]; rows: string[][] };

export type LegalSection = {
  id: string;
  title: string;
  blocks: LegalBlock[];
};

export type LegalDocument = {
  slug: string;
  title: string;
  kicker: string;
  summary: string;
  source: string;
  updated: string;
  note: string;
  highlights: [string, string][];
  sections: LegalSection[];
};

export const legalDocuments: Record<string, LegalDocument> = {
  "acceptable-use-policy": {
    slug: "acceptable-use-policy",
    title: "Acceptable Use Policy",
    kicker: "Network Policy",
    summary:
      "Rules for responsible use of Esited networks, systems, websites, hosting products, and related services.",
    source: "Acceptable Use Policy",
    updated: "August 17, 2015",
    note:
      "Customers and users must follow this policy when using Esited services. The examples below are not exhaustive; contact Esited if a planned use is unclear.",
    highlights: [
      ["Applies broadly", "Covers customers, users, hosted content, network activity, and service operation."],
      ["Abuse response", "Violations may result in content removal, suspension, termination, or investigation."],
      ["Report issues", "Abuse reports should include IP address, time, timezone, and evidence when available."],
    ],
    sections: [
      {
        id: "scope",
        title: "1. Scope and Consent",
        blocks: [
          {
            type: "paragraph",
            text:
              "Esited LLC and its affiliates created this Acceptable Use Policy to provide customers and users a clear understanding of responsible use of the Company's networks, systems, services, websites, and products. By using the services, customers and users agree to be bound by this policy.",
          },
          {
            type: "paragraph",
            text:
              "This policy applies to all customers and users, including users with access to certain services without accounts and users who pay service fees to subscribe to services.",
          },
        ],
      },
      {
        id: "prohibited",
        title: "2. Prohibited Activities",
        blocks: [
          {
            type: "paragraph",
            text:
              "Esited may remove content or terminate access for activity it considers inappropriate, unlawful, harmful, or abusive. Prohibited actions include, but are not limited to:",
          },
          {
            type: "list",
            items: [
              "Selling goods or services that are unlawful where content is posted, received, or delivered.",
              "Posting obscene, lewd, pornographic, excessively violent, harassing, or otherwise objectionable content.",
              "Harming or attempting to harm minors in any way, including child exploitation material.",
              "Transmitting material that threatens or encourages bodily harm or destruction of property.",
              "Encouraging violence against governments, organizations, groups, or individuals.",
              "Posting content that damages or impairs Esited's reputation or goodwill.",
              "Violating copyrights, patents, trademarks, trade secrets, or other intellectual property rights.",
              "Uploading, displaying, distributing, or executing protected works without required permissions.",
              "Deleting or altering copyright notices, author attributes, or copyright management information without permission.",
              "Adding, removing, or modifying identifying network header information to deceive or mislead.",
              "Impersonating any person through forged headers or other identifying information.",
              "Making fraudulent offers, pyramid schemes, ponzi schemes, chain letters, or similar scams.",
              "Posting defamatory, threatening, harassing, private, or emotionally distressing content without permission.",
              "Introducing viruses, worms, Trojan horses, malware, or other harmful code.",
              "Sending unsolicited commercial email or unsolicited bulk email.",
              "Using Esited servers for IRC.",
              "Launching denial-of-service attacks or activity that affects others' ability to use services or the internet.",
              "Hacking, breaching, or testing authentication or security without express authorization from the owner.",
            ],
          },
        ],
      },
      {
        id: "monitoring",
        title: "3. Monitoring and Enforcement",
        blocks: [
          {
            type: "paragraph",
            text:
              "Esited is not obligated to monitor or exercise editorial control over material created or accessed through the services, but reserves the right to do so. If Esited becomes aware that material may violate this policy or expose Esited to civil or criminal liability, Esited may block access, suspend or terminate service, and cooperate with legal authorities or third parties investigating alleged wrongdoing.",
          },
        ],
      },
      {
        id: "reports",
        title: "4. Reporting Violations",
        blocks: [
          {
            type: "paragraph",
            text:
              "Anyone who believes this policy has been violated should send information to the Abuse Department at abuse@vpls.net. When available, include:",
          },
          {
            type: "list",
            items: [
              "The IP address used to commit the alleged violation.",
              "The date and time of the alleged violation, including timezone or GMT offset.",
              "Evidence of the alleged violation.",
            ],
          },
          {
            type: "paragraph",
            text:
              "Esited may revise this policy in its sole discretion without prior notice. Changes will be posted on the website, and users are responsible for periodically reviewing the policy.",
          },
        ],
      },
    ],
  },
  "anti-dmca-policy": {
    slug: "anti-dmca-policy",
    title: "Anti DMCA Policy",
    kicker: "Copyright Response",
    summary:
      "A notice-and-takedown policy for copyright complaints and counter-notifications related to hosted content.",
    source: "DMCA Policy",
    updated: "August 17, 2015",
    note:
      "This page describes how copyright owners may submit notices and how users may respond when material is removed by mistake.",
    highlights: [
      ["Notice required", "Copyright owners should send a proper DMCA notice with complete contact and work details."],
      ["Fast review", "Complaints open an investigation and are commonly reviewed within 48 to 72 hours."],
      ["Counter notices", "Users who believe material was removed incorrectly may submit a proper counter-notification."],
    ],
    sections: [
      {
        id: "overview",
        title: "1. Overview",
        blocks: [
          {
            type: "paragraph",
            text:
              "Esited is an online service provider under Title II of the Digital Millennium Copyright Act, 17 U.S.C. Section 512. This policy outlines the notice-and-takedown process used for copyright complaints and restoration requests.",
          },
        ],
      },
      {
        id: "notice",
        title: "2. Proper DMCA Notice",
        blocks: [
          {
            type: "paragraph",
            text:
              "The DMCA provides a legal procedure to request that an online service provider disable access to material where copyrighted works appear without permission. A proper notice should include:",
          },
          {
            type: "list",
            items: [
              "Identification of the copyright owner or a person authorized to act on behalf of the owner.",
              "Contact information, including name, street address, phone number, and email address.",
              "Identification of the copyrighted work believed to be infringed.",
              "The web URLs where the allegedly infringing material appears. Do not send attached images, PDFs, or other files when URLs can identify the material.",
              "A statement that the complaining party has a good faith belief that use of the material is not authorized by the copyright owner, its agent, or the law.",
              "A statement that the information in the notice is accurate under penalty of perjury.",
              "A physical or electronic signature.",
            ],
          },
        ],
      },
      {
        id: "takedown",
        title: "3. Notice and Takedown Procedure",
        blocks: [
          {
            type: "paragraph",
            text:
              "When Esited receives a proper DMCA notification or otherwise becomes aware of infringement, it will remove or disable access to infringing material as soon as possible. Confirmation is not required before action is taken.",
          },
          {
            type: "paragraph",
            text:
              "If a user believes removed material was lawful, the user may send a proper counter-notification to restore access. Esited will follow the appropriate DMCA provisions when a counter-notification is received.",
          },
        ],
      },
      {
        id: "complaints",
        title: "4. Filing Complaints",
        blocks: [
          {
            type: "paragraph",
            text:
              "DMCA complaints should be sent to net-abuse@esited.com. Please forward URLs to the content so Esited can identify the customer account associated with the material. Most issues are reviewed within 48 hours, though some requests may take up to 72 hours.",
          },
        ],
      },
    ],
  },
  "anti-spam-policy": {
    slug: "anti-spam-policy",
    title: "Anti Spam Policy",
    kicker: "Messaging Policy",
    summary:
      "Rules for bulk email, mailing lists, opt-in subscriptions, and abuse handling on Esited services.",
    source: "Anti SPAM Policy",
    updated: "August 17, 2015",
    note:
      "Spam is against Esited's Terms of Service and may lead to immediate disablement without refund.",
    highlights: [
      ["Opt-in required", "Mailing list subscribers must specifically confirm subscriptions."],
      ["Clear removal", "Every mailing must include simple instructions for opting out."],
      ["Fast complaint review", "Email abuse complaints should include copies of the email and relay transcripts."],
    ],
    sections: [
      {
        id: "overview",
        title: "1. Overview",
        blocks: [
          {
            type: "paragraph",
            text:
              "Esited supports responsible commerce on the internet, but fraud and abuse of network resources harm customers and the internet at large. Spam includes unsolicited email, instant messaging, or other communication sent to a large group, along with off-topic promotional posts in forums, guestbooks, newsgroups, or chat rooms.",
          },
          {
            type: "paragraph",
            text:
              "Spam is strictly against the Terms of Service and may result in immediate disablement without refund for customers who use spam with an Esited account.",
          },
        ],
      },
      {
        id: "subscriptions",
        title: "2. Subscriptions",
        blocks: [
          {
            type: "paragraph",
            text:
              "Mailing list subscribers must specifically opt in. Confirmation should be handled through a confirmation message sent to the subscriber's email address, with a privacy policy URL, a brief mailing list description, and a confirmation URL.",
          },
          {
            type: "list",
            items: [
              "Subscription records must include the confirmed date, time, and IP address.",
              "The use of email addresses must be clearly disclosed at signup or through a prominent privacy policy link.",
              "Outside mailing lists may only be used when the selling organization maintains a publicly viewable privacy policy and records of signup source, time, and IP are kept.",
              "Addresses collected for one purpose may not be used for a substantially different purpose.",
              "All externally sourced subscriptions must be reconfirmed before bulk messages are sent.",
            ],
          },
        ],
      },
      {
        id: "mailing",
        title: "3. Mailing Practices",
        blocks: [
          {
            type: "list",
            items: [
              "Message headers must not be modified to hide the origin of a message.",
              "Undeliverable addresses must be removed after no more than five bounces.",
              "Customers are responsible for affiliates, outside advertisers, and third-party mailing tools they use.",
              "All mailings must provide clear opt-out instructions and reasonable efforts to remove subscribers on request.",
              "Esited email service equipment is located in California and must not be used in violation of California anti-spam laws.",
            ],
          },
        ],
      },
      {
        id: "enforcement",
        title: "4. Spamware, Disablement, and Complaints",
        blocks: [
          {
            type: "paragraph",
            text:
              "Selling software designed specifically to facilitate spamming is prohibited. Esited may disable any account when there is reasonable suspicion that it is being used with spam.",
          },
          {
            type: "paragraph",
            text:
              "Customers must substantially address spam-related inquiries within 72 hours. Complaints related to email should be sent to net-abuse@esited.com with copies of the email and relay transcripts.",
          },
        ],
      },
    ],
  },
  "privacy-policy": {
    slug: "privacy-policy",
    title: "Privacy Policy",
    kicker: "Privacy Center",
    summary:
      "How Esited collects, uses, protects, shares, and updates information related to customers and service users.",
    source: "Privacy Policy",
    updated: "August 17, 2015",
    note:
      "This policy covers information collected through customer accounts and use of Esited services.",
    highlights: [
      ["Collected data", "Information may come from account signup, service use, device details, logs, cookies, and local storage."],
      ["Service use", "Information is used to provide, maintain, protect, and improve Esited services."],
      ["Your choices", "Customers may access, update, and request correction of certain personal information."],
    ],
    sections: [
      {
        id: "intro",
        title: "1. Policy Overview",
        blocks: [
          {
            type: "paragraph",
            text:
              "When customers use Esited services or create an account, Esited may collect information that helps provide better services, support account activity, improve service quality, and protect customer privacy.",
          },
          {
            type: "paragraph",
            text:
              "This Privacy Policy explains what information is collected, why it is collected, how it is used, and what choices customers have to access or update information.",
          },
        ],
      },
      {
        id: "collect",
        title: "2. Information We Collect",
        blocks: [
          {
            type: "paragraph",
            text:
              "Esited collects information customers provide directly, such as name, email address, telephone number, payment details, and account profile information.",
          },
          {
            type: "paragraph",
            text:
              "Esited may also collect information from service use, including device information, log data, IP address, browser information, request time, referral URL, location information, cookies, anonymous identifiers, and local storage data.",
          },
        ],
      },
      {
        id: "use",
        title: "3. How Information Is Used",
        blocks: [
          {
            type: "paragraph",
            text:
              "Information is used to provide, maintain, protect, and improve services; develop new services; protect Esited and users; communicate about services; and provide more relevant service experiences.",
          },
          {
            type: "paragraph",
            text:
              "When customers contact Esited, communications may be retained to help solve issues. Esited may use email addresses to inform customers about service changes or improvements.",
          },
        ],
      },
      {
        id: "choice",
        title: "4. Transparency, Choice, and Access",
        blocks: [
          {
            type: "paragraph",
            text:
              "Customers may have choices to review and control certain account information, edit shared information, control who can access information, remove content from services, or set browser controls for cookies.",
          },
          {
            type: "paragraph",
            text:
              "Esited aims to provide access to personal information and ways to update or delete incorrect information, subject to identity verification, legal requirements, technical limitations, and protection of other users' privacy.",
          },
        ],
      },
      {
        id: "share",
        title: "5. Information Sharing",
        blocks: [
          {
            type: "paragraph",
            text:
              "Esited does not share personal information with companies, organizations, or individuals outside Esited except with consent, with domain administrators, for external processing, or for legal reasons.",
          },
          {
            type: "list",
            items: [
              "Legal reasons may include compliance with law, regulation, legal process, or enforceable government request.",
              "Information may be used to enforce Terms of Service, investigate violations, detect fraud or security issues, and protect rights, property, or safety.",
              "Aggregated, non-personally identifiable information may be shared publicly or with partners.",
            ],
          },
        ],
      },
      {
        id: "security",
        title: "6. Security, Application, and Changes",
        blocks: [
          {
            type: "paragraph",
            text:
              "Esited works to protect information against unauthorized access, alteration, disclosure, or destruction through practices such as encryption, account security tools, review of data practices, and access restrictions for personnel who need information to process it.",
          },
          {
            type: "paragraph",
            text:
              "This policy applies to Esited services and may change from time to time. Changes will be posted on this page, and significant changes may receive more prominent notice.",
          },
        ],
      },
    ],
  },
  "service-level-agreement": {
    slug: "service-level-agreement",
    title: "Service Level Agreement",
    kicker: "Service Commitments",
    summary:
      "Network, hardware replacement, support, remote hands, and service management commitments for eligible Esited services.",
    source: "Network SLA",
    updated: "August 17, 2015",
    note:
      "Credits are subject to eligibility, exclusions, proper request procedure, validation, and the limits described in this SLA.",
    highlights: [
      ["Network goal", "The service goal is 100% availability for eligible current customer accounts."],
      ["Hardware goal", "Defective components in Esited-owned servers target replacement within one hour."],
      ["Support goal", "Written technical support and remote hands requests target 15-minute response acknowledgement."],
    ],
    sections: [
      {
        id: "network",
        title: "1. Network Availability SLA",
        blocks: [
          {
            type: "paragraph",
            text:
              "This SLA applies to customers who ordered virtual web hosting, VPS, dedicated server, or co-location services and whose account is current. Availability means the percentage of a month that customer services are available from the internet, as measured by Esited.",
          },
          {
            type: "paragraph",
            text:
              "Esited's goal is 100% availability. If availability is less than 100%, eligible credits are calculated from the monthly service charge for affected services.",
          },
          {
            type: "table",
            headers: ["Availability", "Credit Percentage"],
            rows: [
              ["99.999% to 100%", "0%"],
              ["99.9% to 99.998%", "5%"],
              ["98% to 99.8%", "10%"],
              ["95% to 97.9%", "25%"],
              ["90% to 94.9%", "50%"],
              ["89.9% or below", "100%"],
            ],
          },
        ],
      },
      {
        id: "hardware",
        title: "2. Hardware Replacement SLA",
        blocks: [
          {
            type: "paragraph",
            text:
              "This SLA applies to eligible customers with virtual web hosting, VPS, or dedicated server services. Replacement Time means the minutes needed to replace defective server components with working components of equal or better quality and performance, as measured by Esited.",
          },
          {
            type: "table",
            headers: ["Replacement Time", "Credit Percentage"],
            rows: [
              ["0 to 60 minutes", "0%"],
              ["60 to 120 minutes", "5%"],
              ["120 to 240 minutes", "10%"],
              ["240 to 480 minutes", "25%"],
              ["480 to 960 minutes", "50%"],
              ["Over 960 minutes", "100%"],
            ],
          },
        ],
      },
      {
        id: "support",
        title: "3. Support and Remote Hands SLA",
        blocks: [
          {
            type: "paragraph",
            text:
              "This SLA applies to eligible customers with virtual web hosting, VPS, dedicated server, co-location, or DDoS protection services. Response Time means the time it takes Esited to acknowledge and dispatch qualified personnel for a written technical support or remote hands request.",
          },
          {
            type: "table",
            headers: ["Response Time", "Credit Percentage"],
            rows: [
              ["0 to 15 minutes", "0%"],
              ["15 to 30 minutes", "5%"],
              ["30 to 60 minutes", "10%"],
              ["60 to 120 minutes", "25%"],
              ["120 to 240 minutes", "50%"],
              ["Over 240 minutes", "100%"],
            ],
          },
        ],
      },
      {
        id: "exceptions",
        title: "4. Exceptions and Credit Requests",
        blocks: [
          {
            type: "paragraph",
            text:
              "Credits do not apply to failures caused by circumstances beyond Esited's reasonable control, scheduled or emergency maintenance, DNS issues outside Esited control, customer acts or omissions, insufficient account or equipment information, inability to contact the customer, or failures outside Esited-controlled network areas.",
          },
          {
            type: "paragraph",
            text:
              "To request a credit, customers must submit a support ticket within ten business days after the breach. The request must include the domain name, account number, date and time of the breach, and must be submitted by an authorized account contact. Validated credits are applied within two billing cycles and are not refundable.",
          },
        ],
      },
      {
        id: "management",
        title: "5. Service Management SLA",
        blocks: [
          {
            type: "paragraph",
            text:
              "For managed dedicated servers or server management for co-location services, Esited aims to provide fast, comprehensive troubleshooting and resolution for supported server issues. Covered areas include Esited-installed hardware, network and power, supported operating systems, common server software, DNS, migrations, security audits, load balancing, and purchased add-on services.",
          },
          {
            type: "paragraph",
            text:
              "Custom software applications are not covered unless a custom service contract has been arranged. Esited may prioritize or limit support where support policies are abused or requests exceed reasonable monthly support allocation.",
          },
        ],
      },
    ],
  },
};

export const legalLinks = [
  { href: "/acceptable-use-policy", label: "Acceptable Use Policy" },
  { href: "/anti-dmca-policy", label: "Anti DMCA Policy" },
  { href: "/anti-spam-policy", label: "Anti Spam Policy" },
  { href: "/privacy-policy", label: "Privacy Policy" },
  { href: "/service-level-agreement", label: "Service Level Agreement" },
  { href: "/terms-of-service", label: "Terms of Service" },
];
